/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMapEvents } from 'react-leaflet';
import clsx from 'clsx';
import { isEmpty, isEqual, omit } from 'lodash';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import Home from '@material-ui/icons/Home';
import { Layers, LayersClear } from '@material-ui/icons';

import 'leaflet/dist/leaflet.css';
import 'react-leaflet-markercluster/dist/styles.min.css';

import { Control, ControlContainer } from './Control';
import Tooltip from '../tooltip/Tooltip';
import { useSessionStorageJson } from '../../utilities/StorageUtilities';
import { useTooltipStyles } from '../charts/tooltip/ChartTooltipStyles';
import SearchContainer from '../search/Search';
import { usePageStateValue } from '../../stores/PageStateStore';
import useMapStyles from './UseMapStyles';
import Sidebar from './sidebar/Sidebar';
import { useVisibleSites, toLatLng, distanceBetween } from './MapUtils';
import SidebarStateProvider, { SidebarStateActionConsumer } from './sidebar/SidebarStateProvider';
import SiteClusterGroup from './markers/SiteClusterGroup';
import useIsPrinting from '../../utilities/PrintUtilities';
import useSize from '../../utilities/UseSize';
import { useMaxMapZoom, MIN_MAP_ZOOM } from '../../services/SystemConfigService';
import { timeRangeFilterId } from '../filters';

const DEFAULT_ZOOM = MIN_MAP_ZOOM;
const DEFAULT_POSITION = [10, 0];
const HOME_LOCATION_TOLERANCE = 0.001;
const TILES_URL = `/query/tile/1/{z}/{x}/{y}`;

function ensureLatLng(location) {
    if (isEmpty(location?.center)) return undefined;
    return location?.isLatLng ? location?.center : toLatLng(location?.center);
}

function GeoJSONTooltip({ name }) {
    const classes = useTooltipStyles();
    return (
        <div>
            <div className={classes.header}>
                <Trans>Region</Trans>
            </div>
            {name}
        </div>
    );
}

function MapEvents({ onOverlayAdd, onOverlayRemove, onZoomEnd, onMoveEnd }) {
    useMapEvents({
        overlayadd: onOverlayAdd,
        overlayremove: onOverlayRemove,
        zoomend: onZoomEnd,
        moveend: onMoveEnd,
    });

    return null;
}

// please, use site map panel on your pages as it is compatible with dashboard framework
export default function SiteMap(props) {
    const {
        id,
        storageKey,
        geojson,
        sites,
        hideSearch,
        onSearchChange,
        filterState,
        options: optionsOverrides,
        onOptionsChange,
        editing,
        saving,
    } = props;
    const timeRangeSelection = filterState?.parameters?.[timeRangeFilterId];
    const isPrinting = useIsPrinting();

    const classes = useMapStyles({ isPrinting });

    const hasGeoJson = !isEmpty(geojson);
    const searchPageStateKey = `${id}Search`;
    const mapZoomStorageKey = `${storageKey}-Zoom`;
    const geoJsonStorageKey = `${storageKey}-geoJson`;
    const mapPositionStorageKey = `${storageKey}-Position`;
    const mapLayersStorageKey = `${storageKey}-Layers`;

    const containerRef = useRef();
    const mapRef = useRef();
    const visibleSites = useVisibleSites(mapRef.current, sites?.data);

    const { width, height } = useSize(containerRef);
    const [hoveredFeatureName, setHoveredFeatureName] = useState();
    const [showGeoJSONTooltip, setShowGeoJSONTooltip] = useState(false);

    const [search, pageStateActions] = usePageStateValue({
        key: searchPageStateKey,
        defaultValue: optionsOverrides?.stickySearchText || '',
    });
    const maxZoom = useMaxMapZoom();
    const [mapZoom, setMapZoom] = useSessionStorageJson(
        mapZoomStorageKey,
        optionsOverrides?.location?.zoom || DEFAULT_ZOOM
    );
    const [showGeoJson, setShowGeoJson] = useSessionStorageJson(geoJsonStorageKey, !!optionsOverrides?.showGeoJson);
    const [mapPosition, setMapPosition] = useSessionStorageJson(
        mapPositionStorageKey,
        ensureLatLng(optionsOverrides?.location) || DEFAULT_POSITION
    );
    const [siteMapLayers, setSiteMapLayers] = useSessionStorageJson(mapLayersStorageKey);
    const [homeLocation, setHomeLocation] = useState({
        center: optionsOverrides?.location?.center || DEFAULT_POSITION,
        zoom: optionsOverrides?.location?.zoom || DEFAULT_ZOOM,
    });
    const [activeSite, setActiveSite] = useState();
    const [options, setOptions] = useState({
        stickySearchText: search,
        location: {
            zoom: mapZoom,
            center: mapPosition,
        },
        showGeoJson,
    });
    const [showHome, setShowHome] = useState(false);
    const enableLayersTT = t`The GeoJSON overlay is hidden. Click to show the GeoJSON overlay`;
    const disableLayersTT = t`Click to hide the GeoJSON overlay`;

    const onSiteMouseLeave = useCallback(() => {
        if (activeSite) {
            setActiveSite(null);
        }
    }, [activeSite]);

    const onSiteMouseEnter = useCallback(
        (site) => {
            if (activeSite !== site) {
                setActiveSite(site);
            }
        },
        [activeSite]
    );

    const handleEditingChange = useRef();
    handleEditingChange.current = () => {
        navigateTo(editing ? homeLocation : options.location);
    };

    const handleOptionChange = useRef();
    handleOptionChange.current = (newOption) => {
        if (editing) {
            const newOptions = {
                ...optionsOverrides,
                ...newOption,
            };
            onOptionsChange(newOptions);
        } else {
            setOptions((currentOptions) => {
                const newOptions = {
                    ...currentOptions,
                    ...newOption,
                };

                return isEqual(currentOptions, newOptions) ? currentOptions : newOptions;
            });
        }
    };

    useEffect(() => {
        handleOptionChange.current({
            stickySearchText: search,
        });
    }, [search]);

    useEffect(() => {
        setShowHome(!isHome.current());
        handleOptionChange.current({
            location: {
                center: mapPosition,
                zoom: mapZoom,
                isLatLng: true,
            },
        });
    }, [mapPosition, mapZoom]);

    useEffect(() => {
        handleOptionChange.current({
            showGeoJson,
        });
    }, [showGeoJson]);

    useLayoutEffect(() => {
        handleEditingChange.current();
    }, [editing]);

    useEffect(() => {
        // If saving, we want to replace the options with the options overrides
        // and set the home location to the new options overrides location.
        if (saving) {
            setOptions(optionsOverrides);
            setHomeLocation(omit(optionsOverrides.location, 'isLatLng'));
        }
    }, [saving, optionsOverrides]);

    useLayoutEffect(() => {
        if (!mapRef.current) return;
        mapRef.current.invalidateSize();
    }, [width, height]);

    const isHome = useRef();
    isHome.current = () => {
        const map = mapRef.current;

        if (!map) return false;

        // Leaflet loses some precision when setting the map location, so even if the map
        // was set to the home location, the lat/lon values will be slightly different.
        // Consequently, we can't just compare them to see if the current map location is
        // equal to the home location.  Instead, we have to determine if they are within
        // some tolerance of each other.  This calculates a location variance to determine
        // if the locations are close enough to be considered equal.
        const bounds = map.getBounds();
        const { lat: swLat, lng: swLng } = bounds.getSouthWest();
        const { lat: neLat, lng: neLng } = bounds.getNorthEast();
        const boundsDistance = distanceBetween([swLat, swLng], [neLat, neLng]);
        const locationDistance = distanceBetween(mapPosition, homeLocation.center);
        const locationVariance = locationDistance / boundsDistance;

        return mapZoom === homeLocation.zoom && locationVariance <= HOME_LOCATION_TOLERANCE;
    };

    function navigateTo(location) {
        const map = mapRef.current;

        if (!map) return;

        const { center, zoom } = location;
        map.flyTo(center, zoom, {
            duration: 0.5,
        });
    }

    function resetLocation() {
        const currentLocation = editing ? optionsOverrides?.location : options?.location;
        if (!isEqual(currentLocation.zoom, homeLocation.zoom)) {
            setMapZoom(homeLocation.zoom);
        }
        if (!isEqual(currentLocation.center, homeLocation.center)) {
            setMapPosition(homeLocation.center);
        }
        navigateTo(homeLocation);
    }

    function handleMapCreated(map) {
        if (map) {
            // Only navigate the first time we get a map ref.  This is because the navigation
            // triggers a new call to this function and can lead to unnecessary recurring navigations.
            const shouldNavigate = !mapRef.current;

            mapRef.current = map;

            // Now that we have a map, set it's initial location.
            if (shouldNavigate) navigateTo(editing ? homeLocation : options.location);
        }
    }

    const handleSearchChange = useCallback(
        (value) => {
            pageStateActions.setValue(searchPageStateKey, value);
            onSearchChange(value);
        },
        [pageStateActions, searchPageStateKey, onSearchChange]
    );

    function handleZoomChange(event) {
        const { lat, lng } = event.target.getCenter();
        const newPosition = [lat, lng];
        setMapZoom(event.target.getZoom());
        setMapPosition(newPosition);
    }

    function handlePositionChange(event) {
        const { lat, lng } = event.target.getCenter();
        const newPosition = [lat, lng];
        setMapPosition(newPosition);
    }

    function handleOverlayAdd(layer) {
        setSiteMapLayers(siteMapLayers, {
            ...siteMapLayers,
            [layer.name.replace(/\s+/g, '')]: { name: layer.name, visible: true },
        });
    }

    function handleOverlayRemove(layer) {
        setSiteMapLayers({
            ...siteMapLayers,
            [layer.name.replace(/\s+/g, '')]: { name: layer.name, visible: false },
        });
    }

    function handleGeoJSONFeature(feature, layer) {
        const featureName = feature?.properties?.name || feature?.properties?.Name;
        if (featureName) {
            layer.on('mouseover', () => {
                setHoveredFeatureName(featureName);
                setShowGeoJSONTooltip(true);
            });
            layer.on('mouseout', () => {
                setShowGeoJSONTooltip(false);
            });
        }
    }

    // Leaflet doesn't seem to support changing the max zoom after initialization, so we have to
    // wait until we've loaded the max zoom setting before initializing the map.
    if (maxZoom === undefined) return null;

    // Render with the options overrides when editing.  Otherwise, render with the current options.
    const renderOptions = editing ? optionsOverrides : options;
    const LayerIcon = renderOptions?.showGeoJson ? Layers : LayersClear;

    return (
        <Tooltip
            open={showGeoJSONTooltip}
            disableHoverListener
            placement="right"
            onClose={() => setShowGeoJSONTooltip(false)}
            title={<GeoJSONTooltip name={hoveredFeatureName} />}
        >
            <div className="flex-container">
                {!hideSearch && (
                    <div className={classes.searchContainer}>
                        <SearchContainer
                            onSearchChange={handleSearchChange}
                            defaultValue={renderOptions?.stickySearchText || ''}
                        />
                    </div>
                )}

                <div ref={containerRef} className={clsx(classes.mapContainer, 'flex-container')}>
                    <SidebarStateProvider id={`${id}-sidebar`}>
                        {!isPrinting && (
                            <Sidebar
                                sites={visibleSites}
                                timeRangeSelection={timeRangeSelection}
                                onSiteMouseEnter={onSiteMouseEnter}
                                onSiteMouseLeave={onSiteMouseLeave}
                            />
                        )}

                        <MapContainer
                            className={clsx('flex-container', classes.map)}
                            center={mapPosition}
                            zoom={mapZoom}
                            minZoom={MIN_MAP_ZOOM}
                            maxZoom={maxZoom}
                            zoomSnap={0.1}
                            attributionControl={false}
                            ref={handleMapCreated}
                        >
                            <MapEvents
                                onOverlayAdd={handleOverlayAdd}
                                onOverlayRemove={handleOverlayRemove}
                                onZoomEnd={handleZoomChange}
                                onMoveEnd={handlePositionChange}
                            />

                            <ControlContainer>
                                {hasGeoJson && (
                                    <Control position="topleft">
                                        <div
                                            className={clsx(
                                                'leaflet-bar',
                                                classes.controlButton,
                                                renderOptions?.showGeoJson && 'active'
                                            )}
                                        >
                                            <a
                                                className="leaflet-bar-part"
                                                onClick={() => setShowGeoJson(!showGeoJson)}
                                                title={i18n._(
                                                    renderOptions?.showGeoJson ? disableLayersTT : enableLayersTT
                                                )}
                                            >
                                                <LayerIcon className={classes.controlIcon} fontSize="small" />
                                            </a>
                                        </div>
                                    </Control>
                                )}

                                {showHome && (
                                    <Control position="topleft">
                                        <div className={clsx('leaflet-bar', classes.controlButton)}>
                                            <a
                                                className="leaflet-bar-part"
                                                onClick={resetLocation}
                                                title={i18n._(t`Reset map location`)}
                                            >
                                                <Home className={classes.controlIcon} fontSize="small" />
                                            </a>
                                        </div>
                                    </Control>
                                )}
                            </ControlContainer>

                            <SidebarStateActionConsumer>
                                {(actions) => (
                                    <SiteClusterGroup
                                        sites={visibleSites}
                                        activeSite={activeSite}
                                        onSiteClick={(site) => actions.activateSite(site)}
                                    />
                                )}
                            </SidebarStateActionConsumer>

                            <TileLayer noWrap url={TILES_URL} />

                            {hasGeoJson && renderOptions?.showGeoJson && (
                                <GeoJSON
                                    className={classes.geoJSON}
                                    data={geojson}
                                    onEachFeature={handleGeoJSONFeature}
                                />
                            )}
                        </MapContainer>
                    </SidebarStateProvider>
                </div>
            </div>
        </Tooltip>
    );
}
